"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import Navbar from "@/app/components/Navbar";
import Footer from "@/app/components/Footer";
import type { JobPosting } from "@/lib/jobs";

export default function CareersPage({ jobs }: { jobs: JobPosting[] }) {
  const jobPostingListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: jobs.map((job, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://www.aureliusco.in/careers/${job.slug}`,
    })),
  };

  return (
    <>
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jobPostingListSchema) }} />
      <main className="min-h-screen px-6 py-32" style={{ background: "#0A0908" }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mx-auto w-full"
        >
          <div className="text-center mb-16">
            <p
              className="text-xs tracking-[0.3em] uppercase mb-3"
              style={{ color: "rgba(201,168,76,0.7)", fontFamily: "'Montserrat', sans-serif" }}
            >
              Careers
            </p>
            <h1
              className="text-4xl md:text-5xl font-light leading-tight mb-6"
              style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}
            >
              We're{" "}
              <span style={{ color: "#C9A84C", fontStyle: "italic" }}>hiring in sales</span>
            </h1>
            <p
              className="text-sm leading-relaxed max-w-lg mx-auto"
              style={{ color: "rgba(245,240,232,0.45)", fontFamily: "'Montserrat', sans-serif" }}
            >
              Two remote Sales Executive roles, open now. Flexible full-time or part-time, performance-based earnings with no cap.
            </p>
          </div>

          {jobs.length === 0 && (
            <p className="text-center text-sm" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>
              No open roles right now — check back soon.
            </p>
          )}

          <div className="flex flex-col gap-5">
            {jobs.map((job, i) => (
              <Link
                key={job.slug}
                href={`/careers/${job.slug}`}
                className="block p-8 transition-all duration-300 group"
                style={{ border: "1px solid rgba(201,168,76,0.15)", background: "rgba(255,255,255,0.015)" }}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p
                      className="text-xs tracking-widest uppercase mb-2"
                      style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {job.company} · Remote
                    </p>
                    <h2
                      className="text-2xl font-light mb-2"
                      style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}
                    >
                      {job.title}
                    </h2>
                    <p
                      className="text-sm leading-relaxed max-w-md"
                      style={{ color: "rgba(245,240,232,0.45)", fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {job.tagline}
                    </p>
                  </div>
                  <span
                    className="text-xs tracking-widest uppercase whitespace-nowrap px-3 py-1.5"
                    style={{ border: "1px solid rgba(201,168,76,0.3)", color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}
                  >
                    View Role →
                  </span>
                </div>
                {i === 0 && (
                  <p
                    className="mt-5 text-xs tracking-wide"
                    style={{ color: "rgba(232,180,90,0.65)", fontFamily: "'Montserrat', sans-serif" }}
                  >
                    ● Only a few seats left this hiring cycle
                  </p>
                )}
              </Link>
            ))}
          </div>

          <p
            className="text-center text-xs mt-14"
            style={{ color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif" }}
          >
            Don't see a fit but want to introduce yourself?{" "}
            <Link href="/contact" style={{ color: "rgba(201,168,76,0.8)" }}>
              Reach out
            </Link>
            .
          </p>
        </motion.div>
      </main>
      <Footer />
    </>
  );
}
